import React, { useCallback } from "react";
import { ChevronDown, LucideIcon, X } from "lucide-react";

interface BuiltInFilterButtonProps {
  label: string;
  icon: LucideIcon;
  activeCount: number;
  isPopoverOpen: boolean;
  onTogglePopover: () => void;
  /** When provided and the chip has an active selection, an X icon replaces the chevron and clears the filter when clicked. */
  onClear?: () => void;
  buttonRef: React.MutableRefObject<HTMLButtonElement | null>;
  shortcutKey?: string;
  /** Rendered inside the button when exactly one item is selected, replacing the label. */
  activeLabel?: string;
}

const BuiltInFilterButton: React.FC<BuiltInFilterButtonProps> = ({
  label,
  icon: Icon,
  activeCount,
  isPopoverOpen,
  onTogglePopover,
  onClear,
  buttonRef,
  shortcutKey,
  activeLabel,
}) => {
  const isActive = activeCount > 0;
  const showActiveLabel = !!activeLabel;
  const showCountBadge = isActive && !showActiveLabel;
  const showClearAffordance = isActive && !!onClear;

  const setRef = useCallback(
    (node: HTMLButtonElement | null) => {
      buttonRef.current = node;
    },
    [buttonRef],
  );

  const handleClear = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onClear?.();
  };

  return (
    <button
      ref={setRef}
      onClick={onTogglePopover}
      aria-expanded={isPopoverOpen}
      aria-haspopup="dialog"
      aria-label={
        showActiveLabel
          ? `Filter by ${label}, currently ${activeLabel}`
          : isActive
            ? `Filter by ${label} (${activeCount} selected)`
            : `Filter by ${label}`
      }
      {...(shortcutKey ? { "data-shortcut": shortcutKey } : {})}
      className={`
        flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold rounded-full transition-all duration-150 whitespace-nowrap flex-shrink-0 border max-w-[16rem]
        ${
          isActive
            ? "border-warm-blue bg-warm-blue/10 text-warm-blue dark:border-warm-blue dark:bg-warm-blue/10 dark:text-warm-blue"
            : "border-warm-border bg-transparent text-warm-text-secondary hover:bg-warm-bg-elevated hover:scale-105"
        }
      `}
    >
      <Icon size={14} className="flex-shrink-0" />
      <span className="truncate">{showActiveLabel ? activeLabel : label}</span>
      {showCountBadge && (
        <span className="bg-warm-blue text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold flex-shrink-0">
          {activeCount}
        </span>
      )}
      {showClearAffordance ? (
        <span
          role="button"
          tabIndex={0}
          aria-label={`Clear ${label} filter`}
          onClick={handleClear}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleClear(e);
            }
          }}
          className="flex-shrink-0 -mr-1 ml-0.5 p-0.5 rounded-full text-warm-blue hover:bg-warm-blue hover:text-white transition-colors duration-150 cursor-pointer flex items-center justify-center"
        >
          <X size={14} />
        </span>
      ) : (
        <ChevronDown
          size={12}
          className={`flex-shrink-0 transition-transform duration-200 ${isPopoverOpen ? "rotate-180" : ""}`}
        />
      )}
    </button>
  );
};

export default BuiltInFilterButton;
